// apps/web/src/pages/BillingSubscriptionsPage.tsx
//
// Global view of all billing subscriptions. CRUD per row.
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, RefreshCw, AlertTriangle, Repeat, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/services/supabase';
import {
  listSubscriptions, createSubscription, updateSubscription, deleteSubscription,
  listBillingProducts,
  type BillingSubscription, type BillingProduct, type BillingSubscriptionInput,
} from '@/services/billing';

interface Row extends BillingSubscription {
  account_name?: string;
  site_name?: string | null;
  product_name?: string;
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const EMPTY: BillingSubscriptionInput = {
  account_id: '',
  site_id: null,
  product_id: '',
  quantity: 1,
  unit_price_override: null,
  description_override: null,
  billing_interval: 'monthly',
  billing_day: 1,
  start_date: todayStr(),
  end_date: null,
  next_billing_date: todayStr(),
  auto_emit_fel: false,
  auto_charge: false,
  status: 'active',
  notes: null,
};

export function BillingSubscriptionsPage() {
  const { hasRole } = usePermissions();
  const canManage = hasRole('admin') || hasRole('super_admin');

  const [rows, setRows]         = useState<Row[]>([]);
  const [products, setProducts] = useState<BillingProduct[]>([]);
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const [sites, setSites]       = useState<{ id: string; name: string; account_id: string }[]>([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [error, setError]       = useState<string | null>(null);
  const [editing, setEditing]   = useState<BillingSubscription | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft]       = useState<BillingSubscriptionInput>(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => { void refresh(); }, []);

  async function refresh() {
    setLoading(true);
    try {
      const [subs, prods, accRes, siteRes] = await Promise.all([
        listSubscriptions(),
        listBillingProducts(),
        supabase.from('accounts').select('id,name').order('name'),
        supabase.from('alarm_sites').select('id,name,account_id').order('name'),
      ]);
      setProducts(prods);
      const accList = (accRes.data ?? []) as { id: string; name: string }[];
      const siteList = (siteRes.data ?? []) as { id: string; name: string; account_id: string }[];
      setAccounts(accList);
      setSites(siteList);
      const accMap = new Map(accList.map(a => [a.id, a.name]));
      const siteMap = new Map(siteList.map(s => [s.id, s.name]));
      const prodMap = new Map(prods.map(p => [p.id, p.name]));
      setRows(subs.map(s => ({
        ...s,
        account_name: accMap.get(s.account_id) ?? '—',
        site_name:    s.site_id ? siteMap.get(s.site_id) ?? null : null,
        product_name: prodMap.get(s.product_id) ?? '—',
      })));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() { setDraft(EMPTY); setCreating(true); }
  function openEdit(s: BillingSubscription) {
    setEditing(s);
    setDraft({
      account_id: s.account_id, site_id: s.site_id, product_id: s.product_id,
      quantity: s.quantity, unit_price_override: s.unit_price_override,
      description_override: s.description_override,
      billing_interval: s.billing_interval, billing_day: s.billing_day,
      start_date: s.start_date, end_date: s.end_date,
      next_billing_date: s.next_billing_date,
      auto_emit_fel: s.auto_emit_fel, auto_charge: s.auto_charge,
      status: s.status, notes: s.notes,
    });
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      if (editing) await updateSubscription(editing.id, draft);
      else await createSubscription(draft);
      setEditing(null); setCreating(false);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    } finally { setSaving(false); }
  }

  async function confirmDelete() {
    if (!deleteId) return;
    try {
      await deleteSubscription(deleteId);
      setDeleteId(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al eliminar');
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (r.account_name ?? '').toLowerCase().includes(q) ||
                            (r.site_name ?? '').toLowerCase().includes(q) ||
                            (r.product_name ?? '').toLowerCase().includes(q));
  }, [rows, search]);

  const sitesForDraft = sites.filter(s => s.account_id === draft.account_id);

  if (!canManage) {
    return (
      <div className="container mx-auto p-6">
        <Card><CardContent className="p-6 text-muted-foreground">Solo administradores.</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-7xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Repeat className="w-6 h-6" /> Contratos / suscripciones
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Servicios recurrentes facturados a cada cuenta.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void refresh()}><RefreshCw className="w-4 h-4" /></Button>
          <Button onClick={openCreate}><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-rose-500/10 text-rose-700 border border-rose-500/30 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-xs text-amber-700 dark:text-amber-300">
        <strong>Política PCI:</strong> ASI Security <strong>no almacena</strong> datos de tarjetas. No existe
        cobro automático recurrente — el cliente ingresa su tarjeta manualmente en cada pago desde la app móvil o el dashboard.
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-end gap-3">
            <CardTitle>Suscripciones ({rows.length})</CardTitle>
            <div className="flex-1 max-w-sm">
              <Label className="text-xs flex items-center gap-1"><Search className="w-3 h-3" /> Buscar</Label>
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cuenta, sitio, producto..." />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Sin suscripciones.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Cuenta</th>
                  <th className="px-3 py-2 font-medium">Sitio</th>
                  <th className="px-3 py-2 font-medium">Producto</th>
                  <th className="px-3 py-2 font-medium text-right">Precio</th>
                  <th className="px-3 py-2 font-medium">Frecuencia</th>
                  <th className="px-3 py-2 font-medium">Día</th>
                  <th className="px-3 py-2 font-medium">Próx.</th>
                  <th className="px-3 py-2 font-medium">Estado</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} className="border-t">
                    <td className="px-3 py-2">{r.account_name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.site_name ?? '—'}</td>
                    <td className="px-3 py-2">{r.product_name}</td>
                    <td className="px-3 py-2 text-right font-mono">{(r.unit_price_override ?? 0).toFixed(2)}</td>
                    <td className="px-3 py-2 capitalize">{r.billing_interval}</td>
                    <td className="px-3 py-2 text-center">{r.billing_day}</td>
                    <td className="px-3 py-2 text-xs">{r.next_billing_date}</td>
                    <td className="px-3 py-2">
                      <Badge variant={r.status === 'active' ? 'secondary' : 'outline'}>{r.status}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteId(r.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create */}
      <Dialog open={creating || !!editing} onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); }}}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar suscripción' : 'Nueva suscripción'}</DialogTitle>
            <DialogDescription>Vinculá una cuenta a un producto y configurá el ciclo.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Cuenta</Label>
              <Select value={draft.account_id} onValueChange={v => setDraft({ ...draft, account_id: v, site_id: null })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Sitio (opcional — para facturación por sitio)</Label>
              <Select value={draft.site_id ?? '__none__'} onValueChange={v => setDraft({ ...draft, site_id: v === '__none__' ? null : v })} disabled={!draft.account_id}>
                <SelectTrigger><SelectValue placeholder="(Sin sitio específico)" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="__none__">(Sin sitio)</SelectItem>
                  {sitesForDraft.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Producto</Label>
              <Select value={draft.product_id} onValueChange={v => {
                const p = products.find(p => p.id === v);
                setDraft({ ...draft, product_id: v, unit_price_override: p?.default_unit_price ?? null });
              }}>
                <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {products.filter(p => p.is_active).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cantidad</Label>
              <Input type="number" step="0.0001" value={draft.quantity}
                onChange={e => setDraft({ ...draft, quantity: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Precio unitario (override)</Label>
              <Input type="number" step="0.01" value={draft.unit_price_override ?? ''}
                onChange={e => setDraft({ ...draft, unit_price_override: e.target.value ? Number(e.target.value) : null })} />
            </div>
            <div>
              <Label>Frecuencia</Label>
              <Select value={draft.billing_interval} onValueChange={v => setDraft({ ...draft, billing_interval: v as 'monthly'|'quarterly'|'yearly' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensual</SelectItem>
                  <SelectItem value="quarterly">Trimestral</SelectItem>
                  <SelectItem value="yearly">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Día de facturación (1-28)</Label>
              <Input type="number" min={1} max={28} value={draft.billing_day}
                onChange={e => setDraft({ ...draft, billing_day: Math.max(1, Math.min(28, Number(e.target.value))) })} />
            </div>
            <div>
              <Label>Inicio</Label>
              <Input type="date" value={draft.start_date}
                onChange={e => setDraft({ ...draft, start_date: e.target.value })} />
            </div>
            <div>
              <Label>Próxima facturación</Label>
              <Input type="date" value={draft.next_billing_date}
                onChange={e => setDraft({ ...draft, next_billing_date: e.target.value })} />
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={draft.status} onValueChange={v => setDraft({ ...draft, status: v as 'active'|'paused'|'cancelled' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activa</SelectItem>
                  <SelectItem value="paused">Pausada</SelectItem>
                  <SelectItem value="cancelled">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fin (opcional)</Label>
              <Input type="date" value={draft.end_date ?? ''}
                onChange={e => setDraft({ ...draft, end_date: e.target.value || null })} />
            </div>
            <div className="col-span-2 grid grid-cols-1 gap-3">
              <div className="flex items-center justify-between border rounded-md px-3 py-2">
                <Label className="text-sm">Auto-emitir FEL tras aprobar</Label>
                <Switch checked={draft.auto_emit_fel} onCheckedChange={v => setDraft({ ...draft, auto_emit_fel: v })} />
              </div>
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                <strong>Cobro automático con tarjeta no disponible.</strong> Por razón legal y de cumplimiento PCI,
                ASI Security <strong>no almacena</strong> datos de tarjetas. El cliente debe ingresar su tarjeta
                desde la app móvil o desde el dashboard cada vez que pague una factura.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !draft.account_id || !draft.product_id}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar suscripción?</DialogTitle>
            <DialogDescription>Esta acción no se puede deshacer.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
